import { Center, Line } from '@react-three/drei';
import { ThreeEvent, useLoader } from '@react-three/fiber';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import * as THREE from 'three';
import { DoubleSide, FrontSide, Group, Shape } from 'three';
import { SVGLoader } from 'three-stdlib';
import { EAttributeType, EAttributeUnit } from '../../@types/AttributeTypes';
import { ISelectedElement } from '../../@types/ContextProviderRelated';
import { EElementType, EInteractionMode, EUnitType } from '../../@types/Enums';
import useDraggableMesh from '../../hooks/useDraggableMesh';
import usePointerKind from '../../hooks/usePointerKind';
import ElementModel from '../../models/standard/ElementModel';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import { useZoomChangeContext } from '../../provider/ZoomChangeProvider';
import { attributeUnitDisplayMap, convertAttributeValue, getCorrespondingUnit } from '../../utils/attributes/attributeUtils';
import { INTERNAL_TO_MM_FACTOR } from '../../utils/constants';
import { calculateScaleAndBoundingBox } from '../../utils/svgUtils';
import CustomTextComponent from './CustomTextComponent';
import ElementDimensionsIndicator from './indicators/ElementDimensionsIndicator';
import RotationSprite from './RotationSprite';

interface ElementProps {
  element: ElementModel;
  position: THREE.Vector3;
  index: number;
  selectedElement: ISelectedElement;
  onClick: (e: ThreeEvent<PointerEvent>) => void;
  onPositionChange?: (newPosition: [number, number, number]) => void;
  onRotationChange?: (newRotation: number) => void;
  onDragEnd?: (element: ElementModel, newPosition: THREE.Vector3) => void;
  opacity?: number;
  disableInteractions?: boolean;
}

const Element: React.FC<ElementProps> = ({
  element,
  position,
  index,
  selectedElement,
  onClick,
  onPositionChange,
  onRotationChange,
  onDragEnd,
  opacity = 1,
  disableInteractions = false,
}) => {
  const intl = useIntl();
  const { locale } = intl;
  const { unitType } = useUnitContext();
  const { staggeredZoomData } = useZoomChangeContext();
  const { interactionMode, setInteractionMode } = useToolbarContext();
  const { showElementDetails } = useMultiSelectToggleContext();
  const { applyTexture } = useBuilding();
  const { isActiveCoarse } = usePointerKind();
  const pivotRef = useRef<Group>(null);
  const helperRef = useRef<Group>(null);
  const elementRef = useRef<Group>(null);
  const meshRefs = useRef<THREE.Mesh[]>([]);

  const isDraggableRef = useRef(true);
  const [isHovered, setIsHovered] = useState(false);
  const [rotationKey, setRotationKey] = useState(0);
  const [showRotationSprite, setShowRotationSprite] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [computedCenter, setComputedCenter] = useState<THREE.Vector2>(new THREE.Vector2(0, 0));

  // These are for Display/SVG calculations only (in MM)
  const widthInMM = useMemo(() => element.width * INTERNAL_TO_MM_FACTOR, [element.width]);
  const lengthInMM = useMemo(() => element.length * INTERNAL_TO_MM_FACTOR, [element.length]);

  const isSelected = selectedElement.selectedElementIndex === index;
  const onTopValue = 5;

  const svg = useLoader(SVGLoader, element.pathToSvg);

  // --- GEOMETRY GENERATION ---
  const { renderShapes, finalScaleX, finalScaleY, textOffset, handleOffset } = useMemo(() => {
    if (element.type === EElementType.TRIANGLE) {
      const a = element.width;
      const b = element.length;
      const c = element.sideC || Math.sqrt(a * a + b * b);

      let cosGamma = (a * a + b * b - c * c) / (2 * a * b);
      cosGamma = Math.max(-1, Math.min(1, cosGamma));
      const sinGamma = Math.sqrt(1 - cosGamma * cosGamma);

      // Points
      const p1 = { x: 0, y: 0 };
      const p2 = { x: a, y: 0 };
      const p3 = { x: b * cosGamma, y: b * sinGamma }; // The "Tip"

      const shape = new Shape();
      shape.moveTo(p1.x, p1.y);
      shape.lineTo(p2.x, p2.y);
      shape.lineTo(p3.x, p3.y);
      shape.lineTo(p1.x, p1.y);

      // --- CENTROID & BOUNDING BOX ---
      const centroidX = (p1.x + p2.x + p3.x) / 3;
      const centroidY = (p1.y + p2.y + p3.y) / 3;

      const minX = Math.min(p1.x, p2.x, p3.x);
      const maxX = Math.max(p1.x, p2.x, p3.x);
      const minY = Math.min(p1.y, p2.y, p3.y);
      const maxY = Math.max(p1.y, p2.y, p3.y);

      const boxCenterX = (minX + maxX) / 2;
      const boxCenterY = (minY + maxY) / 2;

      // Text Offset: Box Center -> Centroid
      const txtOffsetX = centroidX - boxCenterX;
      const txtOffsetY = centroidY - boxCenterY;

      // --- HANDLE OFFSET (Box Center -> Top Vertex) ---
      const handleOffsetX = p3.x - boxCenterX;
      // Add a small buffer (0.5) so it doesn't overlap the line
      const handleOffsetY = p3.y - boxCenterY + 3;

      return {
        renderShapes: [shape],
        finalScaleX: 1,
        finalScaleY: 1,
        textOffset: [txtOffsetX, txtOffsetY, 0] as [number, number, number],
        handleOffset: new THREE.Vector3(handleOffsetX, handleOffsetY, 0),
      };
    }

    // SVG Logic
    const { scaleX, scaleY } = calculateScaleAndBoundingBox(svg.paths, lengthInMM, widthInMM);
    const shapes = svg.paths.flatMap((p) => SVGLoader.createShapes(p));

    return {
      renderShapes: shapes,
      finalScaleX: scaleX,
      finalScaleY: scaleY,
      textOffset: [0, 0, 0] as [number, number, number],
      handleOffset: null, // Return null to use RotationSprite's default placement
    };
  }, [element.type, svg.paths, element.width, element.length, element.sideC, widthInMM, lengthInMM]);

  // --- DESCRIPTION LOGIC ---
  const elementDescription = useMemo(() => {
    let rawText = element.description ?? '';
    if (!element.useAsTextField && element.object) {
      const currentLang = locale.split('-')[0];
      const isImperial = unitType === EUnitType.IMPERIAL;
      let objName = element.object.name;
      if (element.object.localization) {
        objName = element.object.localization[currentLang] || element.object.localization.en || objName;
      }
      let attrText = '';
      if (element.object.attributes && element.object.attributes.length > 0) {
        const firstAttr = element.object.attributes.find((a) => a.predecessorId === null) || element.object.attributes[0];
        if (firstAttr) {
          const key = firstAttr.localization?.[currentLang] || firstAttr.localization?.en || firstAttr.key;
          if (firstAttr.value) {
            let val = firstAttr.value;
            let unitLabel = '';
            if (firstAttr.type === EAttributeType.BOOL) {
              const yes = intl.formatMessage({ id: 'AttributeList.yes', defaultMessage: 'Ja' });
              const no = intl.formatMessage({ id: 'AttributeList.no', defaultMessage: 'Nein' });
              val = val === 'true' ? yes : no;
            } else if (firstAttr.type === EAttributeType.NUMBER) {
              if (firstAttr.unit) {
                const storedUnit = firstAttr.unit as EAttributeUnit;
                const targetUnit = getCorrespondingUnit(storedUnit, isImperial);
                if (targetUnit !== storedUnit) {
                  const num = parseFloat(val);
                  if (!isNaN(num)) {
                    const converted = convertAttributeValue(num, storedUnit, isImperial);
                    val = String(Math.round(converted * 1000) / 1000);
                    unitLabel = attributeUnitDisplayMap[targetUnit] || targetUnit;
                  } else {
                    unitLabel = attributeUnitDisplayMap[storedUnit] || storedUnit;
                  }
                } else {
                  unitLabel = attributeUnitDisplayMap[storedUnit] || storedUnit;
                }
              }
            }
            attrText = `${key}: ${val}`;
            if (unitLabel) {
              attrText += ` ${unitLabel}`;
            }
          } else {
            attrText = key;
          }
        }
      }
      if (objName && attrText) {
        rawText = `${objName}\n${attrText}`;
      } else if (objName) {
        rawText = objName;
      }
    }
    if (rawText) {
      const maxLineLength = 16;
      let formattedText = '';
      const lines = rawText.split('\n');
      lines.forEach((line, i) => {
        let currentLine = '';
        const words = line.split(' ');
        words.forEach((word) => {
          if (currentLine.length + word.length <= maxLineLength) {
            currentLine += (currentLine ? ' ' : '') + word;
          } else {
            formattedText += currentLine + '\n';
            currentLine = word;
          }
        });
        if (currentLine) formattedText += currentLine;
        if (i < lines.length - 1) formattedText += '\n';
      });
      return formattedText;
    }
    return '';
  }, [element.description, element.useAsTextField, element.object, locale, intl, unitType]);

  useEffect(() => {
    if (element.texture?.material && element.width > 0 && element.length > 0) {
      const texture = element.texture.material;
      texture.repeat.set(finalScaleX, -finalScaleY);
      texture.offset.set(element.useAsTextField ? 0 : -(element.texture.offset ?? 0), 0);
      texture.rotation = element.texture.rotation ?? 0;
      meshRefs.current.forEach((mesh) => {
        if (mesh && mesh.material instanceof THREE.MeshBasicMaterial) {
          mesh.material.map = texture;
          mesh.material.needsUpdate = true;
        }
      });
    } else if (!element.texture?.material) {
      meshRefs.current.forEach((mesh) => {
        if (mesh && mesh.material instanceof THREE.MeshBasicMaterial) {
          mesh.material.map = null;
          mesh.material.needsUpdate = true;
        }
      });
    }
  }, [element.texture, element.width, element.length, finalScaleX, finalScaleY, element.useAsTextField]);

  // --- ROTATION HELPER ---
  useEffect(() => {
    if (helperRef.current) {
      helperRef.current.rotation.z = -element.rotation;
    }
  }, [element.rotation]);

  // --- CENTER CALCULATIONS ---
  useEffect(() => {
    if (elementRef.current && helperRef.current) {
      elementRef.current.updateWorldMatrix(true, false);
      const box = new THREE.Box3().setFromObject(elementRef.current);
      if (Math.abs(box.min.x) === Infinity) return;
      const worldCenter = new THREE.Vector3();
      box.getCenter(worldCenter);
      const localCenter = worldCenter.clone();
      helperRef.current.worldToLocal(localCenter);
      setComputedCenter(new THREE.Vector2(localCenter.x, localCenter.y));
      elementRef.current.position.set(-localCenter.x, -localCenter.y, elementRef.current.position.z);
    }
  }, [element.width, element.length, finalScaleX, finalScaleY, renderShapes]);

  // --- DRAG LOGIC ---
  const { isDragging } = useDraggableMesh(
    helperRef,
    null,
    (newPos) => {
      onPositionChange && onPositionChange([newPos.x, newPos.y, 0]);
    },
    isDraggableRef.current && (isHovered || isActiveCoarse) && !disableInteractions,
    false,
    1,
    (finalPos) => {
      if (onDragEnd) onDragEnd(element, finalPos);
    },
  );

  // --- POSITION SYNC ---
  useEffect(() => {
    if (helperRef.current) {
      helperRef.current.position.set(position.x, position.y, element.useAsTextField ? 0 : isSelected || isDragging ? onTopValue : position.z + index * 0.001);
    }
  }, [isSelected, isDragging, index, position, onTopValue, element.useAsTextField]);

  useEffect(() => {
    setShowRotationSprite(!isDragging);
  }, [isDragging]);

  const handleRotationEnd = () => {
    if (helperRef.current) {
      const finalRotation = -helperRef.current.rotation.z;
      const normalizedRotation = ((finalRotation % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
      onRotationChange?.(normalizedRotation);
    }
  };

  const textureToUse = useMemo(() => element.texture?.material || null, [element.texture]);

  const content = useMemo(() => {
    const getClosedPoints = (shape: THREE.Shape) => {
      const pts = shape.getPoints(50).map((v) => [v.x, v.y, 0] as [number, number, number]);
      if (pts.length > 0) {
        pts.push(pts[0]);
      }
      return pts;
    };

    const outlines = renderShapes.map((shape, i) => {
      if (element.useAsTextField) return null;
      return <Line key={`stroke-${i}`} points={getClosedPoints(shape)} color="#000000" lineWidth={2} side={DoubleSide} position={[0, 0, 0.001]} />;
    });

    const hoverOutlines =
      isHovered && !isSelected
        ? renderShapes.map((shape, i) => (
            <Line key={`hover-${i}`} transparent points={getClosedPoints(shape)} color="#33B2E9" lineWidth={2} side={DoubleSide} position={[0, 0, 0.002]} />
          ))
        : null;

    const selectedOutlines = isSelected
      ? renderShapes.map((shape, i) => (
          <Line key={`select-${i}`} transparent points={getClosedPoints(shape)} color="#009fe3" lineWidth={5} side={DoubleSide} position={[0, 0, 0.002]} />
        ))
      : null;

    return (
      <>
        {renderShapes.map((shape, i) => (
          <mesh
            key={`shape-mesh-${i}`}
            ref={(el) => {
              if (el) meshRefs.current[i] = el;
            }}
            raycast={disableInteractions ? () => null : undefined}
          >
            <shapeGeometry args={[shape]} />
            <meshBasicMaterial
              transparent={element.useAsTextField || opacity < 1}
              opacity={element.useAsTextField ? 0 : opacity}
              color={!textureToUse ? (element.texture?.color ?? undefined) || 'white' : undefined}
              map={textureToUse}
              side={FrontSide}
            />
          </mesh>
        ))}
        {outlines}
        {hoverOutlines}
        {selectedOutlines}
      </>
    );
  }, [renderShapes, isHovered, isSelected, element.useAsTextField, element.texture, textureToUse, opacity, disableInteractions]);

  const handleElementClick = (event: ThreeEvent<PointerEvent>) => {
    if (disableInteractions) return;
    if (event.button !== 0 || isDragging) return;
    event.stopPropagation();
    if (interactionMode === EInteractionMode.SET_TEXTURE && onClick) {
      applyTexture(element);
      setInteractionMode(EInteractionMode.NONE);
      onClick(event);
    } else if (onClick) {
      onClick(event);
    }
  };

  const sideCInMM = useMemo(() => (element.sideC || 0) * INTERNAL_TO_MM_FACTOR, [element.sideC]);

  return (
    <group ref={pivotRef} renderOrder={isSelected && !element.useAsTextField ? 9998 : 99 + index} raycast={disableInteractions ? () => null : undefined}>
      <group ref={helperRef} position={[position.x, position.y, element.useAsTextField ? 0 : position.z]}>
        {showElementDetails && !element.useAsTextField && !disableInteractions && (
          <ElementDimensionsIndicator width={widthInMM} length={lengthInMM} rotation={element.rotation} sideC={sideCInMM} />
        )}
        <Center key={`${element.length}-${element.width}-${element.sideC}`}>
          <group
            ref={elementRef}
            scale={[finalScaleX, element.type === EElementType.TRIANGLE ? finalScaleY : -finalScaleY, 1]}
            onPointerDown={disableInteractions ? undefined : handleElementClick}
            onPointerEnter={(e) => {
              if (disableInteractions) return;
              e.stopPropagation();
              setIsHovered(true);
            }}
            onPointerLeave={() => setIsHovered(false)}
          >
            {content}
          </group>
        </Center>
        {elementDescription && (
          <group position={textOffset}>
            <CustomTextComponent
              color={!element.useAsTextField ? '#000000' : element.texture?.color ?? '#000000'}
              text={elementDescription}
              staggeredZoomData={!element.fontSize ? staggeredZoomData : 0}
              fontSize={element.fontSize ?? 0.6}
              rotation={element.rotation}
            />
          </group>
        )}
      </group>
      {!disableInteractions && isSelected && showRotationSprite && !element.useAsTextField && (
        <RotationSprite
          key={`${index}-${rotationKey}`}
          targetRef={helperRef}
          customOffset={handleOffset}
          onStartRotate={() => (isDraggableRef.current = false)}
          onEndRotate={() => {
            isDraggableRef.current = true;
            handleRotationEnd();
          }}
        />
      )}
    </group>
  );
};

export default Element;